import { Alert, AlertDescription, AlertTitle } from "@veil/ui/components/alert";
import { Button } from "@veil/ui/components/button";
import { cn } from "@veil/ui/lib/utils";
import Check from "lucide-react/dist/esm/icons/check";
import CircleAlert from "lucide-react/dist/esm/icons/circle-alert";
import Loader2 from "lucide-react/dist/esm/icons/loader-2";
import RotateCw from "lucide-react/dist/esm/icons/rotate-cw";
import ShieldCheck from "lucide-react/dist/esm/icons/shield-check";
import X from "lucide-react/dist/esm/icons/x";
import { useEffect } from "react";
import type { TimelineStep } from "@/components/operation-step-timeline";
import { type LiveState, useOperationLive } from "@/lib/use-operation-live";
import type { IssuanceCopy } from "../tls-api";

type RowStatus = "pending" | "running" | "done" | "failed" | "skipped";

interface Row {
	key: string;
	label: string;
	status: RowStatus;
	error?: string;
	note?: string;
}

const SEED: LiveState = {
	status: "running",
	exitCode: null,
	finishedAt: null,
	steps: [],
	log: "",
	streamClosed: false,
};

/**
 * Live certificate-issuance timeline. Streams the backend Operation via the
 * shared `useOperationLive` hook (the same SSE endpoint the cluster render
 * flow uses) and renders a compact status timeline with actionable, inline
 * failures + a Retry action.
 *
 * Parametrized by an `IssuanceCopy` bundle so the same component serves both
 * the Let's Encrypt warm-up (`ACME_COPY`) and the Cloudflare Origin CA
 * issuance (`ORIGIN_CA_COPY`).
 */
export function IssuanceProgress({
	operationId,
	hostname,
	copy,
	onStatusChange,
	onRetry,
	retrying,
}: {
	operationId: string;
	hostname: string | null;
	copy: IssuanceCopy;
	/** Bubbles the live operation status up so the caller can gate "Continue". */
	onStatusChange?: (status: string) => void;
	onRetry: () => void;
	retrying?: boolean;
}) {
	const live = useOperationLive(operationId, SEED);
	const { steps, status, log } = live.data;

	useEffect(() => {
		onStatusChange?.(status);
	}, [status, onStatusChange]);

	const failed = status === "failed";
	const succeeded = status === "succeeded";
	const rows = deriveRows(steps, status, copy);

	const errorText = failed ? failedStepError(steps) || log : "";
	const errInfo = failed ? copy.classify(errorText, hostname) : null;

	return (
		<div className="mt-5 rounded-lg border border-border/60 bg-muted/20 p-4">
			<div className="flex items-center gap-2">
				<ShieldCheck className="size-4 text-primary" />
				<h3 className="text-sm font-medium">{copy.heading}</h3>
				{hostname && (
					<code className="ml-auto truncate font-mono text-[0.7rem] text-muted-foreground">
						{hostname}
					</code>
				)}
			</div>

			<ol className="mt-4 space-y-3">
				{rows.map((row) => (
					<StageRow key={row.key} row={row} />
				))}
			</ol>

			{succeeded && (
				<Alert className="mt-4">
					<Check className="size-4" />
					<AlertTitle>{copy.successTitle}</AlertTitle>
					<AlertDescription>{copy.successBody}</AlertDescription>
				</Alert>
			)}

			{failed && errInfo && (
				<Alert variant="destructive" className="mt-4">
					<CircleAlert className="size-4" />
					<AlertTitle>{errInfo.title}</AlertTitle>
					<AlertDescription>
						<span>{errInfo.hint}</span>
						{errorText && (
							<pre className="mt-2 max-h-28 overflow-auto whitespace-pre-wrap rounded bg-destructive/10 p-2 font-mono text-[0.68rem] leading-relaxed">
								{errorText.trim()}
							</pre>
						)}
						<div className="mt-3">
							<Button
								size="sm"
								variant="outline"
								onClick={onRetry}
								disabled={retrying}
							>
								{retrying ? (
									<Loader2 data-icon="inline-start" className="animate-spin" />
								) : (
									<RotateCw data-icon="inline-start" />
								)}
								{copy.retryLabel}
							</Button>
						</div>
					</AlertDescription>
				</Alert>
			)}
		</div>
	);
}

function StageRow({ row }: { row: Row }) {
	return (
		<li className="flex items-start gap-3">
			<span
				className={cn(
					"mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full",
					row.status === "done" && "bg-emerald-500 text-white",
					row.status === "running" && "bg-primary text-primary-foreground",
					row.status === "failed" && "bg-destructive text-white",
					(row.status === "pending" || row.status === "skipped") &&
						"border border-border bg-background",
				)}
				aria-hidden
			>
				{row.status === "done" && <Check className="size-3" />}
				{row.status === "running" && (
					<Loader2 className="size-3 animate-spin" />
				)}
				{row.status === "failed" && <X className="size-3" />}
			</span>
			<div className="flex min-w-0 flex-col">
				<span
					className={cn(
						"text-sm",
						row.status === "pending"
							? "text-muted-foreground"
							: "font-medium text-foreground",
					)}
				>
					{row.label}
				</span>
				{row.status === "running" && row.note && (
					<span className="text-xs italic text-muted-foreground">
						{row.note}
					</span>
				)}
			</div>
		</li>
	);
}

/**
 * Prefer the backend's own structured steps (they carry real labels +
 * statuses + errors). Fall back to the canonical stage scaffold before the
 * stream has produced any steps so the operator always sees the shape of
 * what's about to happen.
 */
function deriveRows(
	steps: TimelineStep[],
	opStatus: string,
	copy: IssuanceCopy,
): Row[] {
	const terminal = opStatus !== "running";

	if (steps.length > 0) {
		return steps.map((s) => ({
			key: s.step,
			label: s.label,
			status:
				terminal && s.status === "running" ? "done" : (s.status as RowStatus),
			error: s.error,
			note: s.note,
		}));
	}

	// No steps yet: scaffold. If the op already failed with no steps, mark the
	// first stage failed; otherwise show the first stage as running.
	return copy.stages.map((stage, i) => ({
		key: stage.key,
		label: stage.label,
		status:
			opStatus === "failed"
				? i === 0
					? "failed"
					: "pending"
				: i === 0
					? "running"
					: "pending",
	}));
}

function failedStepError(steps: TimelineStep[]): string {
	const failedStep = steps.find((s) => s.status === "failed" && s.error);
	return failedStep?.error ?? "";
}
